import { useCallback, FC } from 'react';
import { Accept, ErrorCode, FileRejection, useDropzone } from 'react-dropzone';
import { Box, Center, Flex, Icon, Text, useToast } from '@chakra-ui/react';
import icons, { TIconKeys } from 'shared/icons';
import { dropZoneMultiStyle, uploadedFileStyle } from 'shared/styles';
import { useTranslation } from 'helpers/languages/translations';

type TIcons = (typeof icons)[TIconKeys];

const MDropZoneMulti: FC<{
    chosenFiles: File[] | null;
    handleChangeFile: (file: File[] | null, removed?: boolean) => void;
    maxFiles?: number;
    accept?: Accept;
    icon?: TIcons;
}> = ({ chosenFiles, handleChangeFile, maxFiles = 1, accept, icon }) => {
    const translate = useTranslation();
    const toast = useToast();

    const FilesIcon = icons.files;
    const ExcelIcon = icons.excel;
    const ClearIcon = icons.clear;

    const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
        try {
            const title = fileRejections[0].errors[0].code as ErrorCode;
            toast({ title: translate(`error.upload.${title}`), status: 'warning' });

            return;
        } catch (e) {}

        handleChangeFile(acceptedFiles);
    }, []);

    // if we want to change the number of files to upload we change the maxFiles prop
    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        maxFiles,
        noClick: true,
        accept,
    });

    const removeFile = (index: number) => {
        const files = chosenFiles && [...chosenFiles];
        files?.splice(index, 1);
        return files;
    };

    return (
        <Box>
            <Center
                {...getRootProps({
                    ...dropZoneMultiStyle,
                    border: isDragActive ? '1px solid #0F62FE' : '1px solid #bfb6b6',
                })}
            >
                <input {...getInputProps()} />
                <Center>
                    <Icon as={FilesIcon} w={8} h={8} color="gray.700" mr="16px" />
                    <Flex dir="row" alignItems="baseline">
                        <Text fontSize="18px" color="gray.500">
                            {translate(maxFiles > 1 ? 'dropzone.description.multi' : 'dropzone.description')}
                        </Text>
                        <Text
                            onClick={open}
                            cursor="pointer"
                            textDecor="underline"
                            ml="4px"
                            fontSize="18px"
                            color="blue.500"
                        >
                            {translate('dropzone.button')}
                        </Text>
                    </Flex>
                </Center>
            </Center>
            <Box
                bg={uploadedFileStyle.bg}
                borderX={uploadedFileStyle.borderX}
                borderBottom={uploadedFileStyle.borderX}
                p={5}
                pt={0}
                style={{
                    fontSize: 18,
                    borderColor: isDragActive ? '#0F62FE' : '#bfb6b6',
                    ...(chosenFiles?.length
                        ? { borderBottomRadius: 0 }
                        : { borderBottomLeftRadius: '4px', borderBottomRightRadius: '4px' }),
                }}
            >
                {`${chosenFiles?.length || 0} / ${maxFiles}`}
            </Box>
            <Box maxH={360} overflow="auto">
                {chosenFiles?.map((chosenFile, index) => (
                    <Flex {...uploadedFileStyle} borderRadius="none" key={chosenFile.name}>
                        <Flex>
                            <Icon as={icon || ExcelIcon} w={6} h={6} color="gray.700" />
                            <Text ml="12px">{chosenFile?.name}</Text>
                        </Flex>
                        <Center>
                            <Icon
                                cursor="pointer"
                                onClick={() => handleChangeFile(removeFile(index), true)}
                                as={ClearIcon}
                                w={5}
                                h={5}
                                color="gray.700"
                            />
                        </Center>
                    </Flex>
                ))}
            </Box>
        </Box>
    );
};
export default MDropZoneMulti;
